<template>
  <div ref="searchContainerRef" class="admin-vendeur-search">
    <div class="relative flex-1 min-w-[180px] max-w-sm">
      <div
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <i class="fas fa-store text-gray-400 text-sm"></i>
      </div>
      <input
        v-model.trim="searchTerm"
        type="text"
        autocomplete="off"
        class="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        placeholder="Rechercher un grossiste…"
        @input="onSearchInput"
        @focus="showDropdown = true"
      />
      <div
        v-if="showDropdown && (loading || results.length > 0 || searchTerm.length >= 2)"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto"
      >
        <div v-if="loading" class="px-4 py-3 text-center text-sm text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i> Recherche…
        </div>
        <div v-else-if="results.length > 0" class="divide-y divide-gray-100">
          <button
            v-for="v in results"
            :key="v.id_vendeur"
            type="button"
            class="w-full text-left px-4 py-3 hover:bg-gray-50"
            @click="selectVendeur(v)"
          >
            <span class="font-medium text-gray-900 block truncate">
              {{ v.nomBoutique || `Vendeur #${v.id_vendeur}` }}
            </span>
            <span class="text-sm text-gray-500 block truncate">
              {{ v.nomSociete }}
              <span v-if="v.email"> · {{ v.email }}</span>
            </span>
            <span class="text-xs text-gray-400 font-mono">#{{ v.id_vendeur }}</span>
          </button>
        </div>
        <div
          v-else-if="searchTerm.length >= 2"
          class="px-4 py-3 text-center text-gray-500 text-sm"
        >
          Aucun grossiste trouvé
        </div>
      </div>
    </div>

    <AdminVendeurAccountModal
      v-model:is-visible="modalVisible"
      :vendeur-id="selectedVendeurId"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useAdminEfpStore } from '@/stores/adminEfp'
import AdminVendeurAccountModal from '@/components/adminEFP/AdminVendeurAccountModal.vue'
import type { AdminEfpVendeur } from '@/api/adminEfp'

const adminEfp = useAdminEfpStore()

const searchTerm = ref('')
const results = ref<AdminEfpVendeur[]>([])
const showDropdown = ref(false)
const loading = ref(false)
const searchContainerRef = ref<HTMLElement | null>(null)

const modalVisible = ref(false)
const selectedVendeurId = ref<number | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

async function fetchResults() {
  const term = searchTerm.value.trim()
  if (term.length < 2) {
    results.value = []
    return
  }
  loading.value = true
  try {
    results.value = await adminEfp.autocompleteVendeurs(term)
  } catch {
    results.value = []
  } finally {
    loading.value = false
  }
}

function onSearchInput() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchResults, 250)
}

async function selectVendeur(v: AdminEfpVendeur) {
  showDropdown.value = false
  searchTerm.value = ''
  results.value = []
  selectedVendeurId.value = v.id_vendeur
  modalVisible.value = true
}

function handleClickOutside(e: MouseEvent) {
  if (searchContainerRef.value?.contains(e.target as Node)) return
  showDropdown.value = false
}

watch(showDropdown, (open) => {
  if (open) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(modalVisible, (visible) => {
  if (!visible) selectedVendeurId.value = null
})
</script>

<style scoped>
.admin-vendeur-search {
  flex: 1;
  display: flex;
  justify-content: center;
  max-width: 20rem;
  min-width: 0;
}

@media (max-width: 768px) {
  .admin-vendeur-search {
    display: none;
  }
}
</style>
